import * as React from "react"
import {useCallback, useEffect, useMemo, useRef, useState} from "react"
import {useDispatch, useSelector} from "react-redux"
import {useHistory, useLocation} from "react-router-dom"
import {StateType} from "../../store/store"
import {ID_SYSTEM_COMPANY} from "../../constants/constants"
import {center} from "../../helps/tableHelper"
import {filterFromString} from "../../helps/filterFromString"
import deepEqual from "deep-equal"
import {filterToString} from "../../helps/filterToString"
import {deleteUin, getUinList, setUinList, setUinListFilter, setWSUinList} from "../../store/uinListReducer"
import {Helmet} from "react-helmet-async"
import {Button, Card, Col, FloatButton, Modal, Row, Table} from "antd"
import {FormSelect} from "../../components/Form/FormSelect"
import {isActiveOptionsSelect, isAttachDeviceOptionsSelect, isSolvedOptionsSelect, limitOptionsSelect} from "../../helps/optionsSelect"
import {Loading} from "../../components/Loading/Loading"
import {BsTrash} from "react-icons/bs"
import {useGetCompanies} from "../../hooks/useFetchHooks"
import {FormInput} from "../../components/Form/FormInput"

export const UinList = () =>
{
    const dispatch = useDispatch()
    const location = useLocation()
    const history = useHistory()
    const profile = useSelector((state: StateType) => state.appReducer.profile)
    const uinList = useSelector((state: StateType) => state.uinListReducer.uinList)
    const filter = useSelector((state: StateType) => state.uinListReducer.filter)
    const ws = useSelector((state: StateType) => state.uinListReducer.ws)
    const getCompanyParams = useMemo(() => ({excludeSystem: true}), [])
    const companies = useGetCompanies(getCompanyParams)
    const controller = useMemo(() => new AbortController(), [])
    const [selectedRowKeys, setSelectedRowKeys] = useState<Array<number>>([])
    const [loading, setLoading] = useState(false)
    const [showModal, setShowModal] = useState(false)

    const rowSelection = useMemo(() => ({
        selectedRowKeys,
        onChange: (newSelectedRowKeys: Array<any>) => setSelectedRowKeys(newSelectedRowKeys),
    }), [selectedRowKeys])

    const columns = useMemo(() =>
    {
        const cols = []

        if (profile?.idCompany === ID_SYSTEM_COMPANY) cols.push({
            title: "Компания",
            dataIndex: "nameCompany",
            key: "nameCompany",
            align: center,
        })

        cols.push(
            {
                title: "Группа",
                dataIndex: "nameObjectGroup",
                key: "nameObjectGroup",
                align: center,
            },
            {
                title: "Объект",
                dataIndex: "nameObject",
                key: "nameObject",
                align: center,
            },
            {
                title: "Оборудование",
                dataIndex: "nameDevice",
                key: "nameDevice",
                align: center,
            },
            {
                title: "UIN",
                dataIndex: "uin",
                key: "uin",
                align: center,
            },
        )

        return cols
    }, [profile])

    useEffect(() =>
    {
        const filterString = filterFromString(location.search)
        if (filterString && !deepEqual(filterString, filter))
        {
            dispatch(setUinListFilter({...filterString}))
        }
        else if (location.search !== filterToString(filter))
        {
            history.replace({search: filterToString(filter)})
        }
    }, [dispatch, filter, history, location])

    const getUinListFunc = useCallback((withoutLoadingIndicator = false) =>
    {
        if (location?.search)
        {
            dispatch(getUinList({
                ...filterFromString(location.search),
                withoutLoadingIndicator,
                controller,
            }))
        }
    }, [dispatch, location, controller])

    useEffect(() =>
    {
        getUinListFunc()

        return () =>
        {
            dispatch(setUinList(null))
        }
    }, [dispatch, getUinListFunc])

    useEffect(() =>
    {
        return () =>
        {
            controller.abort()
        }
    }, [dispatch, controller])

    useEffect(() =>
    {
        if (ws)
        {
            getUinListFunc(true)
        }
        dispatch(setWSUinList(null))
    }, [dispatch, ws, getUinListFunc])

    const deleteUinsFunc = useCallback(async () =>
    {
        try
        {
            setLoading(true)
            const {payload}: any = await dispatch(deleteUin({
                id: selectedRowKeys,
                controller,
            }))
            if (payload)
            {
                setSelectedRowKeys([])
                setShowModal(false)
            }
        } finally
        {
            setLoading(false)
        }
    }, [selectedRowKeys, controller])

    const data = useMemo(() => uinList?.data?.map(i =>
    {
        return {
            key: i.id,
            id: i.id,
            nameCompany: i.nameCompany,
            uin: i.uin,
            nameObjectGroup: i.nameObjectGroup,
            nameObject: i.nameObject,
            idDevice: i.idDevice,
            nameDevice: i.nameDevice,
        }
    }), [uinList])

    const companyOptions = useMemo(() => companies?.data?.map(i => ({label: i.name, value: i.id})) || [], [companies])

    return (
        <div className="tabled">
            <Helmet>
                <title>Список UIN</title>
            </Helmet>
            <Row gutter={[24, 0]}>
                <Col xs={24}>
                    <Card variant="borderless"
                          className="criclebox tablespace mb-24"
                          title={
                              <div className="table-title">
                                  <div>Список UIN</div>
                                  <Button onClick={() => history.push("/psLeakUinListForm")} type="link" size="middle" style={{marginTop: 5}}>
                                      Добавить
                                  </Button>
                              </div>
                          }
                          extra={
                              <div className="table-title-extra">
                                  <FormInput placeholder="Поиск"
                                             containerStyle={{marginBottom: 0, width: "auto", minWidth: 100}}
                                             value={filter?.search}
                                             name="search"
                                             onChange={(e) => history.replace({search: filterToString({...filter, search: e.target.value, offset: 0})})}
                                             allowClear
                                  />

                                  {
                                      companies
                                          ? <FormSelect value={filter?.idCompany}
                                                        containerStyle={{marginBottom: 0, width: "auto", minWidth: 100}}
                                                        dropdownStyle={{minWidth: "min-content"}}
                                                        onChange={(value) => history.replace({search: filterToString({...filter, idCompany: value, offset: 0})})}
                                                        options={companyOptions}
                                                        placeholder="Компания"
                                                        allowClear
                                                        placement="bottomRight"
                                          />
                                          : null
                                  }

                                  <FormSelect value={filter?.status}
                                              containerStyle={{marginBottom: 0, width: "auto", minWidth: 100}}
                                              dropdownStyle={{minWidth: "min-content"}}
                                              onChange={(value) => history.replace({search: filterToString({...filter, status: value, offset: 0})})}
                                              options={isAttachDeviceOptionsSelect}
                                              placeholder="Статус"
                                              allowClear
                                              placement="bottomRight"
                                  />

                                  <FormSelect value={filter?.limit}
                                              containerStyle={{marginBottom: 0, width: "auto"}}
                                              dropdownStyle={{minWidth: "min-content"}}
                                              onChange={(value) => history.replace({search: filterToString({...filter, limit: value, offset: 0})})}
                                              options={limitOptionsSelect}
                                              placement="bottomRight"
                                  />
                              </div>
                          }
                    >
                        <div className="table-responsive">
                            {
                                uinList
                                    ? <Table columns={columns}
                                             dataSource={data}
                                             pagination={filter.limit ? {
                                                 position: uinList?.data?.length >= 10 ? ["bottomLeft", "topLeft"] : ["bottomLeft"],
                                                 pageSize: filter.limit,
                                                 hideOnSinglePage: true,
                                                 total: uinList.total,
                                                 current: filter?.offset / filter?.limit + 1,
                                                 onChange: (page, pageSize) =>
                                                 {
                                                     const obj = {
                                                         limit: pageSize,
                                                         offset: (page - 1) * pageSize,
                                                     }
                                                     history.replace({search: filterToString({...filter, ...obj})})
                                                 },
                                                 showSizeChanger: false,
                                             } : false}
                                             className="ant-border-space"
                                             onRow={({uin, idDevice}) => ({
                                                 onClick: () => history.push(idDevice ? `/psLeakDevice/${idDevice}` : `/psLeakDeviceForm`, {uin}),
                                                 onMouseDown: (e) =>
                                                 {
                                                     const params = new URLSearchParams({uin: uin ?? ""}).toString()
                                                     e.button === 1 && window.open(idDevice ? `/psLeakDevice/${idDevice}` : `/psLeakDeviceForm?${params}`, "_blank", "noopener,noreferrer")
                                                 },
                                             })}
                                             rowSelection={rowSelection}
                                    />
                                    : <Loading/>
                            }
                        </div>
                    </Card>
                </Col>
            </Row>

            <Modal title="Вы уверены что хотите удалить записи?"
                   centered
                   open={showModal}
                   onOk={deleteUinsFunc}
                   onCancel={() =>
                   {
                       setShowModal(false)
                   }}
                   okText="Удалить"
                   okButtonProps={{loading}}
            />

            {
                selectedRowKeys?.length > 0
                    ? <FloatButton onClick={() => setShowModal(true)}
                                   tooltip={<div>Удалить</div>}
                                   type="primary"
                                   icon={<BsTrash/>}
                    />
                    : null
            }
        </div>
    )
}